import React, { useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useRemoveExperience, useRemoveTicket } from '~/hooks/apis/accommodation/useBundles';

import { Bundle, BundleOffer } from '~/services/AccommodationService';

import AccommodationBundleAttachExperienceDialog from '../dialogs/AccommodationBundleAttachExperienceDialog';
import AccommodationBundleExperienceEditDialog from '../dialogs/AccommodationBundleExperienceEditDialog';
import AccommodationBundleTicketDialog from '../dialogs/AccommodationBundleTicketDialog';

interface Props {
  bundle: Bundle;
}

type TicketRow = NonNullable<BundleOffer['tickets']>[number];

function AccommodationBundleExperiencesTab({ bundle }: Props) {
  const [attachExperienceOpen, setAttachExperienceOpen] = useState(false);
  const [ticketDialogState, setTicketDialogState] = useState<{
    offerId: string;
    ticket?: TicketRow;
  } | null>(null);
  const [editExperienceOffer, setEditExperienceOffer] = useState<BundleOffer | null>(null);

  const removeExperienceMutation = useRemoveExperience();
  const removeTicketMutation = useRemoveTicket();
  const [pendingExperienceIds, setPendingExperienceIds] = useState<Set<string>>(new Set());
  const [pendingTicketIds, setPendingTicketIds] = useState<Set<string>>(new Set());

  const experiences = (bundle.offers ?? []).filter((o) => o.entityType === 'EXPERIENCE');

  const handleRemoveExperience = (offerId: string) => {
    if (pendingExperienceIds.has(offerId)) return;
    if (!window.confirm('Remove this experience and all its tickets?')) return;
    setPendingExperienceIds((prev) => new Set(prev).add(offerId));
    removeExperienceMutation.mutate(
      { bundleId: bundle.id, offerId },
      {
        onSettled: () =>
          setPendingExperienceIds((prev) => {
            const next = new Set(prev);
            next.delete(offerId);
            return next;
          }),
      },
    );
  };

  const handleRemoveTicket = (offerId: string, ticketId: string) => {
    if (pendingTicketIds.has(ticketId)) return;
    if (!window.confirm('Remove this ticket?')) return;
    setPendingTicketIds((prev) => new Set(prev).add(ticketId));
    removeTicketMutation.mutate(
      { bundleId: bundle.id, offerId, ticketId },
      {
        onSettled: () =>
          setPendingTicketIds((prev) => {
            const next = new Set(prev);
            next.delete(ticketId);
            return next;
          }),
      },
    );
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" onClick={() => setAttachExperienceOpen(true)}>
          + Attach Experience
        </Button>
      </Box>

      {experiences.length === 0 && (
        <Alert severity="info">No experiences attached. Click "+ Attach Experience" to add one.</Alert>
      )}

      <Stack spacing={2}>
        {experiences.map((offer) => (
          <Card key={offer.id} variant="outlined">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography variant="h6">{offer.experienceName ?? offer.entityId}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Experience ID: {offer.entityId}
                  </Typography>
                  {offer.experienceDescription && (
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      {offer.experienceDescription}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <IconButton onClick={() => setEditExperienceOffer(offer)} aria-label="Edit experience">
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleRemoveExperience(offer.id)}
                    disabled={pendingExperienceIds.has(offer.id)}
                    aria-label="Remove experience"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">Tickets</Typography>
                <Button size="small" onClick={() => setTicketDialogState({ offerId: offer.id })}>
                  + Add Ticket
                </Button>
              </Box>

              {(offer.tickets ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No tickets configured. Add at least one for this experience to surface in availability.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Product ID</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell align="right">LOS</TableCell>
                      <TableCell align="right">Lead Days</TableCell>
                      <TableCell align="center">Per Occupant</TableCell>
                      <TableCell align="center">Per Night</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(offer.tickets ?? []).map((ticket) => (
                      <TableRow key={ticket.id}>
                        <TableCell>{ticket.name ?? '—'}</TableCell>
                        <TableCell>{ticket.productId}</TableCell>
                        <TableCell align="right">{ticket.price ?? '—'}</TableCell>
                        <TableCell>{ticket.currency ?? '—'}</TableCell>
                        <TableCell align="right">{ticket.los ?? '—'}</TableCell>
                        <TableCell align="right">{ticket.leadDays ?? '—'}</TableCell>
                        <TableCell align="center">{ticket.perOccupant ? 'Yes' : 'No'}</TableCell>
                        <TableCell align="center">{ticket.perNight ? 'Yes' : 'No'}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => setTicketDialogState({ offerId: offer.id, ticket })}
                            aria-label="Edit ticket"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveTicket(offer.id, ticket.id)}
                            disabled={pendingTicketIds.has(ticket.id)}
                            aria-label="Remove ticket"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>

      {attachExperienceOpen && (
        <AccommodationBundleAttachExperienceDialog
          bundleId={bundle.id}
          open={attachExperienceOpen}
          onClose={() => setAttachExperienceOpen(false)}
        />
      )}

      {ticketDialogState && (
        <AccommodationBundleTicketDialog
          bundleId={bundle.id}
          offerId={ticketDialogState.offerId}
          ticket={ticketDialogState.ticket}
          open={!!ticketDialogState}
          onClose={() => setTicketDialogState(null)}
        />
      )}

      {editExperienceOffer && (
        <AccommodationBundleExperienceEditDialog
          bundleId={bundle.id}
          offer={editExperienceOffer}
          open={!!editExperienceOffer}
          onClose={() => setEditExperienceOffer(null)}
        />
      )}
    </Box>
  );
}

export default AccommodationBundleExperiencesTab;
